import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type Notification,
} from '../../api/client';

const REFRESH_INTERVAL_MS = 45_000;

export default function NotificationBell() {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await listNotifications();
      setNotifications(res.notifications);
      setUnreadCount(res.unreadCount);
    } catch {
      // Silently ignore — the bell just won't update this cycle.
    }
  }, []);

  useEffect(() => {
    void refresh();
    const id = setInterval(() => void refresh(), REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (dropdownRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  const handleMarkRead = async (id: string) => {
    setNotifications((list) => list.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await markNotificationRead(id);
    } catch {
      void refresh();
    }
  };

  const handleMarkAllRead = async () => {
    setNotifications((list) => list.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await markAllNotificationsRead();
    } catch {
      void refresh();
    }
  };

  const rect = buttonRef.current?.getBoundingClientRect();

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative flex items-center justify-center h-9 w-9 rounded-md border border-[#e8ddd0] dark:border-lc-border bg-transparent text-[#241c14] dark:text-lc-text hover:bg-white/60 dark:hover:bg-lc-bg/60 transition-colors"
        title={t('notifications.bell')}
        aria-label={t('notifications.bell')}
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-lc-pink px-1 text-[10px] font-bold text-white dark:text-lc-ink">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open &&
        rect &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[9999] w-80 max-h-[70vh] overflow-y-auto rounded-md border border-[#e8ddd0] dark:border-lc-border bg-white dark:bg-lc-bg shadow-lg"
            style={{ top: rect.bottom + 8, right: window.innerWidth - rect.right }}
          >
            <div className="flex items-center justify-between px-3 py-2 border-b border-[#e8ddd0] dark:border-lc-border">
              <span className="text-xs font-semibold text-[#241c14] dark:text-lc-text">
                {t('notifications.title')}
              </span>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => void handleMarkAllRead()}
                  className="text-[11px] font-medium text-lc-pink hover:underline"
                >
                  {t('notifications.markAllRead')}
                </button>
              )}
            </div>

            {notifications.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-[#7a6b5d] dark:text-lc-faint">
                {t('notifications.empty')}
              </div>
            ) : (
              <ul>
                {notifications.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => void handleMarkRead(n.id)}
                      className={`w-full text-left px-3 py-2.5 border-b border-[#e8ddd0] dark:border-lc-border last:border-b-0 hover:bg-[#f5eff5] dark:hover:bg-lc-surface transition-colors ${
                        n.read ? 'opacity-60' : ''
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {!n.read && <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-lc-pink" />}
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-[#241c14] dark:text-lc-text truncate">
                            {n.title}
                          </p>
                          <p className="text-[11px] text-[#7a6b5d] dark:text-lc-faint line-clamp-2">
                            {n.message}
                          </p>
                          <p className="mt-1 text-[10px] text-[#a89a8c] dark:text-lc-faint">
                            {new Date(n.createdAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>,
          document.body
        )}
    </>
  );
}
